import { LegalLayout, H, P, Callout } from "@/components/LegalLayout";
import { legalBusinessIdentity as id } from "@/data/legalBusinessIdentity";
import { SITE } from "@/data/content";
import { useRegion } from "@/hooks/useRegion";
import { isTagalogSelected } from "@/data/tagalogTranslations";

const formatDate = (iso, locale = "en-US") =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" });

export default function BusinessInformation() {
  const { isPH } = useRegion();
  const isTL = isPH && isTagalogSelected();
  const dateLocale = isTL ? "fil-PH" : "en-US";
  return (
    <LegalLayout
      title={isTL ? "Pagpaparehistro ng Negosyo" : "Business Registration"}
      subtitle={isTL ? "Mga detalye ng pagpaparehistro ng negosyo sa Pilipinas" : isPH ? "Philippine business registration details" : "Registered operator identity for Philippine and international services"}
      updated={isTL ? "Setyembre 2026" : "September 2026"}
    >
      <P>
        {isTL
          ? `${id.businessName} ay isang rehistradong solong pagmamay-ari (Single Proprietorship) na pag-aari ni ${id.proprietor} at nagpapatakbo alinsunod sa mga batas ng Republika ng Pilipinas.`
          : `${id.businessName} is a registered ${id.businessType.toLowerCase()} of ${id.proprietor}, operating under the laws of the ${id.jurisdiction}.`}
      </P>

      {!isPH && (
        <Callout>
          Registered country: Republic of the Philippines. MySiteCare primarily serves business customers in the
          Philippines and also offers digital website services to business customers in Europe and other international markets.
        </Callout>
      )}

      <H>{isTL ? "Rehistradong Pangalan ng Negosyo" : "Registered Business Name"}</H>
      <P>{id.formalBusinessName}</P>

      <H>Registered / Business Address</H>
      <P data-testid="business-information-registered-address">{id.registeredBusinessAddress}</P>

      <H>Public Brand</H>
      <P>MySiteCare</P>

      <H>{isTL ? "Pag-aari at Pinapatakbo Ni" : "Owned and Operated By"}</H>
      <P>{id.proprietor}</P>

      <H>{isTL ? "Uri ng Negosyo" : "Business Structure"}</H>
      <P>{isTL ? "Solong Pagmamay-ari (Single Proprietorship)" : id.businessType}</P>

      <H>{isTL ? "Pagpaparehistro ng Pangalan ng Negosyo sa DTI" : "DTI Business Name Registration"}</H>
      <P>
        {isTL
          ? `${id.businessName} ay pangalan ng negosyo ni ${id.proprietor} na nakarehistro sa DTI sa ilalim ng Business Name Blg. ${id.dti.businessNameNumber}, na may bisa mula ${formatDate(id.dti.registeredFrom, dateLocale)} hanggang ${formatDate(id.dti.registeredUntil, dateLocale)}.`
          : `${id.businessName} is a DTI-registered business name of ${id.proprietor} under Business Name No. ${id.dti.businessNameNumber}, valid from ${formatDate(id.dti.registeredFrom, dateLocale)} through ${formatDate(id.dti.registeredUntil, dateLocale)}.`}
      </P>
      <Callout>
        {isTL
          ? `Ang pagpaparehistrong ito ay may saklaw na Lungsod / Munisipalidad (City / Municipality). Tinutukoy nito kung saan maaaring mairehistro ang pangalan ng negosyo sa napiling teritoryo; hindi nito nililimitahan kung saan maaaring magserbisyo ang ${id.businessName}.`
          : `This registration has ${id.dti.territorialScope} territorial scope. That governs the registrability of the business name within the selected territory — it does not limit where ${id.businessName} may serve customers.`}
      </Callout>

      <H>{isTL ? "Pagpaparehistro sa BIR" : "BIR Registration"}</H>
      <P>
        {isTL
          ? `${id.businessName} ay nakarehistro sa BIR (petsa ng pagpaparehistro: ${formatDate(id.bir.registrationDate, dateLocale)}) sa ilalim ng line of business na “${id.bir.lineOfBusiness}”.`
          : `${id.businessName} is BIR-registered (registration date ${formatDate(id.bir.registrationDate, dateLocale)}) under the line of business “${id.bir.lineOfBusiness}”.`}
      </P>
      <P data-testid="bir-registration-status">
        {isTL
          ? "Kumpleto ang pagpaparehistro sa BIR. Ipinapakita sa ibaba ang opisyal na BIR Registration Seal Badge; hindi ipinapakita rito ang buong Form 2303 o sensitibong impormasyon ng nagbabayad ng buwis."
          : "BIR registration is complete. The official BIR Registration Seal Badge is displayed below; the full Form 2303 and sensitive taxpayer information are not displayed here."}
      </P>
      <figure className="my-6 max-w-[550px]" data-testid="bir-registration-seal-badge">
        <a href="/compliance/ph/bir/MySiteCare_BIR_Registration_Seal_Badge.png" target="_blank" rel="noopener noreferrer" aria-label="Open the BIR Registration Seal Badge at full size" data-testid="bir-registration-seal-enlarge-link">
          <img src="/compliance/ph/bir/MySiteCare_BIR_Registration_Seal_Badge.png" alt="Official BIR Registration Seal Badge" className="h-auto w-full" />
        </a>
        <figcaption className="mt-3 text-sm text-slate-600">Official BIR Registration Seal Badge — scan the QR code to verify MySiteCare&apos;s registration with the Bureau of Internal Revenue.</figcaption>
      </figure>

      <H>{isTL ? "Bansang Pinagrehistruhan" : "Registered Country"}</H>
      <P>{isTL ? "Republika ng Pilipinas" : id.jurisdiction}</P>

      <H>{isTL ? "Pakikipag-ugnayan" : "Contact"}</H>
      <P>
        {isTL ? "Email" : "Email"}: <a href={`mailto:${SITE.email}`} className="text-[#10B981] hover:underline">{SITE.email}</a><br />
        {isTL ? "Telepono" : "Phone"}: <a href={`tel:${SITE.phoneHref}`} className="text-[#10B981] hover:underline">{SITE.phone}</a>
      </P>

      <H>Website</H>
      <P>
        <a href={isPH ? `${id.website}/ph` : id.website} className="text-[#10B981] hover:underline">{isPH ? `${id.website}/ph` : id.website}</a>
      </P>
    </LegalLayout>
  );
}
